import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/design-system/components/ui/form';
import { Input } from '@repo/design-system/components/ui/input';
import { cn } from '@repo/design-system/lib/utils';
import { useFormContext } from 'react-hook-form';
import type { FormSection } from '../config/form-config';

interface FormFieldsProps {
  sections: FormSection[];
  className?: string;
}

export function FormFields({ sections, className = '' }: FormFieldsProps) {
  const form = useFormContext();

  // If no form context is available, render a simpler version
  if (!form) {
    return (
      <div className={cn('space-y-8', className)}>
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="space-y-1">
            {section.title && (
              <div className="font-medium text-muted-foreground text-sm">
                {section.title}
              </div>
            )}
            <div className="space-y-4">
              {section.fields.map((field) => (
                <div key={field.id} className="space-y-2">
                  <label
                    className="font-medium text-sm"
                    htmlFor={`preview-${field.id}`}
                  >
                    {field.label}
                  </label>
                  <Input
                    id={`preview-${field.id}`}
                    type={field.type}
                    placeholder={field.placeholder}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-8', className)}>
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="space-y-3">
          {section.title && (
            <div className="font-medium text-muted-foreground text-sm">
              {section.title}
            </div>
          )}
          <div className="space-y-4">
            {section.fields.map((field) => (
              <FormField
                key={field.id}
                control={form.control}
                name={field.id}
                rules={{
                  required:
                    field.id !== 'businessname'
                      ? `${field.label} is required`
                      : false,
                  validate: (value) => {
                    if (field.id === 'email' && value) {
                      const emailRegex =
                        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                      return emailRegex.test(value) || 'Invalid email address';
                    }
                    return true;
                  },
                }}
                render={({ field: formField }) => (
                  <FormItem>
                    <FormLabel>{field.label}</FormLabel>
                    <FormControl>
                      <Input
                        {...formField}
                        id={`preview-${field.id}`}
                        type={field.type}
                        placeholder={field.placeholder}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
